import type { ComponentType } from 'react';

/**
 * How a client module plugs into the shell (frozen; docs/MODULE-GUIDE-client.md explains it).
 * The shell composes Home, the Play hub, Me, the header and the player profile from what the
 * modules contribute here, so no module edits a shared screen.
 *
 * Everything that renders is lazy (`() => import('./x')`), so the first paint only loads the
 * shell. A module's index.ts must stay light: data and lazy references, no screen imports.
 */

export const CLIENT_MODULE_NAMES = [
  'platform',
  'push',
  'cards',
  'players',
  'games',
  'stats',
  'combine',
  'tournaments',
  'studio',
  'gamelab',
  'sessions',
] as const;
export type ClientModuleName = (typeof CLIENT_MODULE_NAMES)[number];

export type TabId = 'home' | 'stats' | 'play' | 'crew' | 'me';

/** A lazily loaded component module: `() => import('./screens/Thing')` with a default export. */
export type Lazy<P = object> = () => Promise<{ default: ComponentType<P> }>;

export interface ModuleRoute {
  /** Absolute path, react-router syntax: '/runs/:id', '/stats/*'. Must be unique app-wide. */
  path: string;
  /** The screen. Its default export renders inside the chosen layout. */
  screen: Lazy;
  /** 'tabs' (default): header plus tab bar. 'bare': full screen (keeper pad, board, live run). */
  layout?: 'tabs' | 'bare';
  /** Which tab lights up. Defaults from the path prefix. */
  tab?: TabId;
}

/** A Home card. Lower `order` shows first (sessions' Next run is 0). */
export interface HomeCard {
  id: string;
  order: number;
  component: Lazy;
}

/** A square or wide tile on the Play hub grid. Pure data, so the hub renders without loading module code. */
export interface PlayHubTile {
  id: string;
  order: number;
  title: string;
  /** One short line under the title ("2 players", "First to 21"). */
  sub?: string;
  to: string;
  /** Fill from the pastel ramp. */
  color: 'lavender' | 'sky' | 'mint' | 'butter' | 'peach' | 'white';
  icon?: IconName;
  size?: 'square' | 'wide';
}

/** A strip on the Play hub above the tiles (the Runs strip from sessions). */
export interface PlaySection {
  id: string;
  order: number;
  component: Lazy;
}

/** A block on the Me screen (studio entry, push preferences, devices). */
export interface MeSection {
  id: string;
  order: number;
  title?: string;
  component: Lazy;
}

/** A block on the player profile (/p/:id). Gets the player id. */
export interface ProfileSection {
  id: string;
  order: number;
  title?: string;
  component: Lazy<{ playerId: string }>;
}

/** Something in the header's right slot (the push bell). Keep it 44px square or smaller. */
export interface HeaderAction {
  id: string;
  order: number;
  component: Lazy;
}

/** Named extension points other modules' screens render with <Slot name="..." />. */
export interface SlotProps {
  'h2h.extra': { a: string; b: string };
  'profile.extra': { playerId: string };
  'session.recap': { sessionId: string };
  'admin.section': Record<never, never>;
}
export type SlotName = keyof SlotProps;
export const SLOT_NAMES = [
  'h2h.extra',
  'profile.extra',
  'session.recap',
  'admin.section',
] as const satisfies readonly SlotName[];

export interface SlotEntry<N extends SlotName> {
  order?: number;
  component: Lazy<SlotProps[N]>;
}

export type IconName =
  | 'home'
  | 'stats'
  | 'ball'
  | 'crew'
  | 'me'
  | 'bell'
  | 'plus'
  | 'chevron'
  | 'copy'
  | 'share'
  | 'gear'
  | 'trophy'
  | 'flask'
  | 'calendar'
  | 'whistle'
  | 'camera'
  | 'card'
  | 'bolt'
  | 'check'
  | 'close';

export interface ClientModule {
  name: ClientModuleName;
  routes?: readonly ModuleRoute[];
  homeCards?: readonly HomeCard[];
  playHubTiles?: readonly PlayHubTile[];
  playSections?: readonly PlaySection[];
  meSections?: readonly MeSection[];
  profileSections?: readonly ProfileSection[];
  headerActions?: readonly HeaderAction[];
  slots?: { readonly [N in SlotName]?: SlotEntry<N> };
  /**
   * Runs once after sign-in: register outbox streams, offline prefetches, bus listeners.
   * Keep it cheap and synchronous; lazy-import anything heavy inside.
   */
  init?(): void;
}

export function defineClientModule(m: ClientModule): ClientModule {
  return m;
}
